import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ExternalLink,
  FlaskConical,
  ImageOff,
  LoaderCircle,
  RefreshCw,
  Sparkles,
  TriangleAlert,
} from 'lucide-react'
import {
  compareAdminStudioBakeoff,
  getAdminStudioBakeoff,
  submitAdminStudioBakeoffItem,
} from '../../api/generated/admin-studio-generation/admin-studio-generation'
import type {
  StudioBakeoffBatchRead,
  StudioBakeoffComparisonAsset,
  StudioBakeoffComparisonRead,
  StudioBakeoffItemRead,
  StudioBakeoffOperation,
} from '../../api/generated/model'
import { Badge, type BadgeProps } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { parseApiProblem } from '../../lib/api-problem'
import { USE_REAL_API } from '../../lib/api'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import {
  magnificTestPanelI18n,
  type MagnificTestPanelStrings,
} from '../../i18n/admin/magnific-test-panel'
import { errorDetailMessage } from '../../api/error-detail'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const ACTIVE_STATUSES = new Set(['submitted', 'generating'])

const OPERATION_META: Record<StudioBakeoffOperation, { name: string; short: string }> = {
  image_relight: { name: 'Image Relight', short: 'Relight' },
  seedream_v4_5_edit: { name: 'Seedream 4.5 Edit', short: 'Seedream' },
  seedream_v5_pro_edit: { name: 'Seedream 5 Pro Edit', short: 'Seedream' },
  mystic: { name: 'Mystic', short: 'Mystic' },
}

function badgeVariant(status: StudioBakeoffItemRead['status']): BadgeProps['variant'] {
  if (status === 'pending_review') return 'success'
  if (status === 'failed' || status === 'blocked') return 'destructive'
  if (ACTIVE_STATUSES.has(status)) return 'info'
  return 'neutral'
}

function hasActiveItem(batch: StudioBakeoffBatchRead | undefined): boolean {
  return Boolean(batch?.items.some((item) => ACTIVE_STATUSES.has(item.status)))
}

async function readBatch(batchId: string): Promise<StudioBakeoffBatchRead> {
  const response = await getAdminStudioBakeoff(batchId)
  if (response.status !== 200) throw new Error(errorDetailMessage(response.data))
  return response.data
}

async function readComparison(batchId: string): Promise<StudioBakeoffComparisonRead> {
  const response = await compareAdminStudioBakeoff(batchId)
  if (response.status !== 200) throw new Error(errorDetailMessage(response.data))
  return response.data
}

function failureMessage(item: StudioBakeoffItemRead, t: MagnificTestPanelStrings): string | null {
  if (item.status !== 'failed') return null
  if (item.failure_code === 'studio_output_unsafe' && item.attempt_count === 1) {
    return t.unsafeOutput
  }
  return item.failure_code ? t.failureCode(item.failure_code) : t.failedBeforeReview
}

export function MagnificTestPanel({ providerReady = true }: { providerReady?: boolean }) {
  const t = useT(magnificTestPanelI18n)
  const [batchInput, setBatchInput] = useState('')
  const [loadedBatchId, setLoadedBatchId] = useState('')
  const [confirmItem, setConfirmItem] = useState<StudioBakeoffItemRead | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const batchQuery = useQuery({
    queryKey: ['admin', 'studio-generation', 'batch', loadedBatchId],
    queryFn: () => readBatch(loadedBatchId),
    enabled: USE_REAL_API && Boolean(loadedBatchId),
    refetchInterval: (query) => (hasActiveItem(query.state.data) ? 3_000 : false),
  })
  const comparisonQuery = useQuery({
    queryKey: ['admin', 'studio-generation', 'comparison', loadedBatchId],
    queryFn: () => readComparison(loadedBatchId),
    enabled: USE_REAL_API && Boolean(loadedBatchId) && Boolean(batchQuery.data),
    // Let the comparison packet stop its own polling only after it has observed
    // the terminal response that carries the freshly ingested Drive asset.
    refetchInterval: (query) =>
      query.state.data?.results.some((result) => ACTIVE_STATUSES.has(result.status))
        ? 3_000
        : false,
  })

  const submit = useMutation({
    mutationFn: async (item: StudioBakeoffItemRead) => {
      const response = await submitAdminStudioBakeoffItem(
        item.id,
        { execution_mode: 'magnific' },
        { headers: { 'Idempotency-Key': `media-generation-ui:${item.id}` } },
      )
      if (response.status !== 202) throw new Error(errorDetailMessage(response.data))
      return response.data
    },
    onMutate: () => setSubmitError(null),
    onSuccess: (_, item) => {
      toast.success(t.submittedToast(OPERATION_META[item.operation].name), {
        description: t.submittedDescription,
        audience: 'admin',
      })
    },
    onError: (error: unknown) => {
      const problem = parseApiProblem(error, t.submitProblem)
      setSubmitError(problem.message)
      toast.error(t.notConfirmedToast, {
        description: t.notConfirmedDescription,
        audience: 'admin',
      })
    },
    onSettled: async () => {
      setConfirmItem(null)
      await Promise.all([batchQuery.refetch(), comparisonQuery.refetch()])
    },
  })

  const inputValid = UUID_RE.test(batchInput.trim())
  const batch = batchQuery.data
  const comparison = comparisonQuery.data

  return (
    <section className="overflow-hidden rounded-lg bg-[var(--color-surface)] ring-1 ring-[var(--color-border)]" aria-labelledby="magnific-test-title">
      <div className="border-b border-[var(--color-border)] px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--color-info)_12%,var(--color-surface))] text-[var(--color-info)]">
            <FlaskConical size={18} aria-hidden="true" />
          </div>
          <div>
            <h2 id="magnific-test-title" className="text-sm font-semibold text-[var(--color-text)]">
              {t.title}
            </h2>
            <p className="mt-0.5 text-xs leading-5 text-[var(--color-text-muted)]">
              {t.subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-5 px-5 py-5">
        {!USE_REAL_API && (
          <div className="rounded-md bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] px-3 py-2 text-xs text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]">
            {t.simulationWarning}
          </div>
        )}
        {!providerReady && USE_REAL_API && (
          <div className="rounded-md bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] px-3 py-2 text-xs text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]">
            {t.credentialsWarning}
          </div>
        )}

        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault()
            setSubmitError(null)
            if (!inputValid) return
            const nextBatchId = batchInput.trim()
            if (nextBatchId === loadedBatchId) {
              void Promise.all([batchQuery.refetch(), comparisonQuery.refetch()])
            } else {
              setLoadedBatchId(nextBatchId)
            }
          }}
        >
          <label className="min-w-0 flex-1">
            <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.batchId}</span>
            <input
              value={batchInput}
              onChange={(event) => setBatchInput(event.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              spellCheck={false}
              autoComplete="off"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-base text-[var(--color-text)] outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)] disabled:bg-[var(--color-surface-muted)] sm:text-[13px]"
              disabled={!USE_REAL_API}
            />
          </label>
          <Button type="submit" variant="outline" disabled={!USE_REAL_API || !inputValid || batchQuery.isFetching}>
            {batchQuery.isFetching ? (
              <LoaderCircle size={14} className="mr-1.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <RefreshCw size={14} className="mr-1.5" aria-hidden="true" />
            )}
            {t.loadBatch}
          </Button>
        </form>

        {batchQuery.error && (
          <InlineError message={parseApiProblem(batchQuery.error, t.loadBatchError).message} />
        )}
        {submitError && <InlineError message={submitError} />}

        {batch && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-y border-[var(--color-border)] py-3 text-xs">
              <span className="min-w-0 font-mono text-[var(--color-text-muted)] [overflow-wrap:anywhere]">{batch.id}</span>
              <div className="flex items-center gap-2">
                <Badge variant="neutral">{t.batchStatus(batch.status)}</Badge>
                {hasActiveItem(batch) && (
                  <span className="inline-flex items-center gap-1.5 text-[var(--color-text-muted)]" role="status">
                    <LoaderCircle size={13} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    {t.refreshing}
                  </span>
                )}
              </div>
            </div>

            {comparisonQuery.error && (
              <InlineError message={parseApiProblem(comparisonQuery.error, t.comparisonError).message} />
            )}

            <div className="space-y-4">
              {batch.items.map((item) => (
                <OperationCard
                  key={item.id}
                  item={item}
                  source={comparison?.source ?? null}
                  result={comparison?.results.find((entry) => entry.operation === item.operation)?.asset ?? null}
                  providerReady={providerReady}
                  submitting={submit.isPending && submit.variables?.id === item.id}
                  onRun={() => setConfirmItem(item)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={Boolean(confirmItem)} onOpenChange={(open) => !open && !submit.isPending && setConfirmItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.runTitle(confirmItem ? OPERATION_META[confirmItem.operation].name : null)}</DialogTitle>
            <DialogDescription>
              {t.runDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] px-3 py-2 text-xs leading-5 text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]">
            {confirmItem?.estimated_provider_credits != null && (
              <span className="block font-semibold">
                {t.estimatedCost(confirmItem.estimated_provider_credits)}
              </span>
            )}
            {t.enqueueNote}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={submit.isPending} onClick={() => setConfirmItem(null)}>
              {t.cancel}
            </Button>
            <Button
              type="button"
              variant="signal"
              disabled={submit.isPending || !confirmItem}
              onClick={() => confirmItem && submit.mutate(confirmItem)}
            >
              {submit.isPending && <LoaderCircle size={14} className="mr-1.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {t.confirmPaid}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

function OperationCard({
  item,
  source,
  result,
  providerReady,
  submitting,
  onRun,
}: {
  item: StudioBakeoffItemRead
  source: StudioBakeoffComparisonAsset | null
  result: StudioBakeoffComparisonAsset | null
  providerReady: boolean
  submitting: boolean
  onRun: () => void
}) {
  const t = useT(magnificTestPanelI18n)
  const meta = OPERATION_META[item.operation]
  const failure = failureMessage(item, t)

  return (
    <article className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]/40 p-3 sm:p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)]">{meta.name}</h3>
          <p className="mt-0.5 text-[11px] text-[var(--color-text-muted)] [overflow-wrap:anywhere]">{t.recipe(item.recipe_id)}</p>
        </div>
        <Badge variant={badgeVariant(item.status)}>{t.statuses[item.status]}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <AssetFrame asset={source} label={t.reference} alt={t.referenceAlt(meta.name)} />
        <AssetFrame asset={result} label={t.generated} alt={t.generatedAlt(meta.name)} />
      </div>

      <div className="mt-3 flex flex-col gap-3 border-t border-[var(--color-border)] pt-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1 text-[11px] text-[var(--color-text-muted)]">
          <div><span className="font-medium text-[var(--color-text)]">{t.method}</span> {meta.short}</div>
          <div>
            <span className="font-medium text-[var(--color-text)]">{t.credits}</span>{' '}
            {item.estimated_provider_credits != null
              ? t.estimatedCredits(item.estimated_provider_credits)
              : t.reportedSeparately}
          </div>
          <div><span className="font-medium text-[var(--color-text)]">{t.status}</span> {t.statuses[item.status]}</div>
          {failure && <p className="max-w-2xl text-[var(--color-danger)]">{failure}</p>}
        </div>
        {item.status === 'planned' && (
          <Button
            type="button"
            size="sm"
            variant="signal"
            disabled={!providerReady || submitting}
            onClick={onRun}
            aria-label={t.runAria(meta.name)}
          >
            {submitting ? (
              <LoaderCircle size={14} className="mr-1.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <Sparkles size={14} className="mr-1.5" aria-hidden="true" />
            )}
            {t.runInMagnific}
          </Button>
        )}
      </div>
    </article>
  )
}

function AssetFrame({ asset, label, alt }: { asset: StudioBakeoffComparisonAsset | null; label: string; alt: string }) {
  const t = useT(magnificTestPanelI18n)
  return (
    <figure className="min-w-0">
      <div className="aspect-square overflow-hidden rounded-md border border-[var(--color-border)] bg-white">
        {asset ? (
          <a href={asset.review_url} target="_blank" rel="noreferrer" className="group relative block h-full w-full">
            <img
              src={asset.review_url}
              alt={alt}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-contain"
            />
            <span className="absolute right-2 top-2 inline-flex rounded bg-slate-950/65 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100">
              <ExternalLink size={13} aria-hidden="true" />
              <span className="sr-only">{t.openFullSize}</span>
            </span>
          </a>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-2 text-center text-[11px] text-slate-700">
            <ImageOff size={22} aria-hidden="true" />
            {t.notInDrive}
          </div>
        )}
      </div>
      <figcaption className="mt-1.5 text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
        {label}
      </figcaption>
    </figure>
  )
}

function InlineError({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-[color-mix(in_srgb,var(--color-danger)_10%,var(--color-surface))] px-3 py-2 text-xs leading-5 text-[var(--color-danger)] ring-1 ring-[color-mix(in_srgb,var(--color-danger)_42%,var(--color-border))]" role="alert">
      <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}
